import { Button } from "@/components/ui/button";
import { HasAccess } from "@/components/common/has-access";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronDown } from "lucide-react";
import { UserProfile } from "@/pages/users/types/user";
import {
  computeJourneyTag,
  computeTierTag,
  computeTransitionTag,
} from "../utils/user-calculations";
import { useUpdateBehaviorTags } from "../hooks/use-update-behavior-tags";
import { useUpdateUserDetails } from "../hooks/use-update-user-details";
import { useEffect, useMemo, useState } from "react";

const BEHAVIOR_TAG_OPTIONS = [
  { id: "TOUGH_USER", label: "Tough User" },
  { id: "BAD_EXPERIENCE_FROM_RH", label: "Bad Experience from RH" },
  { id: "REFUND_SENSITIVE", label: "Refund Sensitive User" },
  { id: "CHANGE_SENSITIVE", label: "Change Sensitive User" },
];

const RISK_OPTIONS = [
  { id: "LOW_RISK", label: "Low Risk" },
  { id: "MEDIUM_RISK", label: "Medium Risk" },
  { id: "HIGH_RISK", label: "High Risk" },
];

function Badge({
  children,
  className = "",
  title,
}: {
  children: React.ReactNode;
  className?: string;
  title?: string;
}) {
  return (
    <span
      title={title}
      className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${className}`}
    >
      {children}
    </span>
  );
}

function riskBadgeClass(risk: string) {
  if (risk === "HIGH_RISK") return "bg-red-100 text-red-800";
  if (risk === "MEDIUM_RISK") return "bg-yellow-100 text-yellow-800";
  if (risk === "LOW_RISK") return "bg-green-100 text-green-800";
  return "bg-gray-100 text-gray-800";
}

function transitionTooltip(logs?: { old_value: any; new_value: any }[]) {
  if (!logs || logs.length === 0) return undefined;
  const names = logs
    .map((l) => l?.new_value?.name)
    .filter(Boolean)
    .map(String);
  if (names.length === 0) return undefined;
  return names.join(" → ");
}

export function UserTagControls({ user }: { user: UserProfile }) {
  const { mutate: updateBehaviorTags, isPending: isBehaviorUpdating } =
    useUpdateBehaviorTags();
  const { mutate: updateUserDetails, isPending: isRiskUpdating } =
    useUpdateUserDetails();

  const behaviorTags = Array.isArray(user.behavior_tags) ? user.behavior_tags : [];
  const riskId = user.risk_category || null;
  const [draftBehaviorTags, setDraftBehaviorTags] = useState<string[]>(behaviorTags);

  useEffect(() => {
    setDraftBehaviorTags(behaviorTags);
  }, [user.id, user.behavior_tags]);

  const toggleBehavior = (tagId: string) => {
    setDraftBehaviorTags((prev) =>
      prev.includes(tagId) ? prev.filter((t) => t !== tagId) : [...prev, tagId]
    );
  };

  const setRisk = (nextRisk: string) => {
    updateUserDetails({
      user_id: user.id,
      risk_category: nextRisk,
    });
  };

  const hasBehaviorChanges = useMemo(() => {
    if (draftBehaviorTags.length !== behaviorTags.length) return true;
    const a = [...draftBehaviorTags].sort();
    const b = [...behaviorTags].sort();
    return a.some((item, idx) => item !== b[idx]);
  }, [draftBehaviorTags, behaviorTags]);

  const applyBehaviorTags = () => {
    updateBehaviorTags({ userId: user.id, tags: draftBehaviorTags });
  };

  return (
    <HasAccess permission="write">
      <div className="flex items-center gap-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
              disabled={isRiskUpdating}
            >
              Risk <ChevronDown className="h-4 w-4 ml-1.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Risk level</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuRadioGroup
              value={riskId || ""}
              onValueChange={(v) => v && setRisk(v)}
            >
              {RISK_OPTIONS.map((o) => (
                <DropdownMenuRadioItem key={o.id} value={o.id}>
                  {o.label}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
              disabled={isBehaviorUpdating}
            >
              Behavior <ChevronDown className="h-4 w-4 ml-1.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Behavior tags</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {BEHAVIOR_TAG_OPTIONS.map((o) => (
              <DropdownMenuCheckboxItem
                key={o.id}
                checked={draftBehaviorTags.includes(o.id)}
                onSelect={(e) => e.preventDefault()}
                onCheckedChange={() => toggleBehavior(o.id)}
              >
                {o.label}
              </DropdownMenuCheckboxItem>
            ))}
            <DropdownMenuSeparator />
            <div className="px-2 py-1.5">
              <Button
                size="sm"
                className="w-full"
                onClick={applyBehaviorTags}
                disabled={!hasBehaviorChanges || isBehaviorUpdating}
              >
                Update
              </Button>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </HasAccess>
  );
}

export function UserTagsSection({ user }: { user: UserProfile }) {
  const behaviorTags = Array.isArray(user.behavior_tags) ? user.behavior_tags : [];

  const tierTag = computeTierTag(user.bundles_usage || []);
  const journeyTag = computeJourneyTag(user.bundles_usage || []);
  const transitionTag = computeTransitionTag(user.transition_count);
  const transitionTitle = transitionTooltip(user.transition_logs);

  const riskId = user.risk_category || null;
  const riskLabel =
    RISK_OPTIONS.find((o) => o.id === riskId)?.label ?? (riskId ? String(riskId) : null);

  return (
    <div className="pt-2">
      <div className="flex items-center gap-2 overflow-x-auto whitespace-nowrap pb-1">
        {journeyTag && <Badge className="bg-blue-100 text-blue-800">{journeyTag}</Badge>}
        {tierTag && <Badge className="bg-purple-100 text-purple-800">{tierTag}</Badge>}
        {transitionTag && (
          <Badge className="bg-amber-100 text-amber-800" title={transitionTitle}>
            {transitionTag}
          </Badge>
        )}
        {riskLabel && (
          <Badge className={riskBadgeClass(riskId || "")}>
            {riskLabel}
          </Badge>
        )}
        {behaviorTags.map((t) => {
          const label = BEHAVIOR_TAG_OPTIONS.find((o) => o.id === t)?.label ?? t;
          return (
            <Badge key={t} className="bg-orange-100 text-orange-800">
              {label}
            </Badge>
          );
        })}
      </div>
    </div>
  );
}

